import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/services/seniors')({
  component: SeniorsPage,
  head: () => ({
    meta: [
      { title: 'Seniors services & aged care — Warren-Blackwood Electorate Guide' },
      {
        name: 'description',
        content:
          'Plain-English guide for older Warren-Blackwood residents: My Aged Care, Commonwealth Home Support, residential aged care per shire, Age Pension, WA Seniors Card, and when an MP can help.',
      },
      { property: 'og:title', content: 'Seniors services & aged care — Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'My Aged Care, Support at Home, Age Pension, WA Seniors Card and local aged care across all six shires.',
      },
    ],
  }),
});

const SHIRE_QUICK = [
  { id: 'augusta-margaret-river', name: 'Augusta-Margaret River' },
  { id: 'denmark', name: 'Denmark' },
  { id: 'boyup-brook', name: 'Boyup Brook' },
  { id: 'bridgetown-greenbushes', name: 'Bridgetown-Greenbushes' },
  { id: 'nannup', name: 'Nannup' },
  { id: 'manjimup', name: 'Manjimup' },
] as const;

function SeniorsPage() {
  const h2: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.5rem',
    color: 'var(--rivergum)',
    marginTop: 'var(--sp-xl)',
    marginBottom: 'var(--sp-sm)',
    borderBottom: '1px solid var(--light-border)',
    paddingBottom: '0.3rem',
    letterSpacing: '0.02em',
  };
  const h3: React.CSSProperties = {
    fontFamily: 'var(--font-heading)',
    fontSize: '1.1rem',
    color: 'var(--ink)',
    marginTop: 'var(--sp-md)',
    marginBottom: '0.4rem',
    letterSpacing: '0.02em',
  };
  const p: React.CSSProperties = {
    color: 'var(--ink)',
    lineHeight: 1.65,
    marginBottom: 'var(--sp-sm)',
  };
  const link: React.CSSProperties = { color: 'var(--green-mid)', fontWeight: 600 };
  const callout = (bg: string, border: string): React.CSSProperties => ({
    background: bg,
    border: `1px solid ${border}`,
    borderRadius: 8,
    padding: 'var(--sp-md)',
    marginBottom: 'var(--sp-md)',
  });

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 760,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          textTransform: 'uppercase',
          letterSpacing: '0.12em',
          fontSize: '0.75rem',
          fontWeight: 700,
          color: 'var(--green-mid)',
          margin: 0,
          marginBottom: '0.35rem',
        }}
      >
        Services · Seniors &amp; aged care
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5vw, 2.75rem)',
          color: 'var(--green)',
          margin: 0,
          marginBottom: 'var(--sp-md)',
          lineHeight: 1.1,
        }}
      >
        Seniors services &amp; aged care
      </h1>
      <p
        style={{
          color: 'var(--ink-mid)',
          fontStyle: 'italic',
          fontSize: '1.05rem',
          marginBottom: 'var(--sp-lg)',
          lineHeight: 1.5,
        }}
      >
        30.4% of Warren-Blackwood residents are aged 60 or over — the largest
        service-age group in the electorate. This page is the plain-English
        starting point for aged care, Age Pension, the WA Seniors Card, and the
        local providers you're most likely to deal with.
      </p>

      <div style={callout('#eef5fb', '#b9d4e8')}>
        <strong style={{ color: '#1b4f72' }}>Quick contacts</strong>
        <p style={{ ...p, margin: '0.4rem 0 0', fontSize: '0.95rem' }}>
          My Aged Care — <a href="tel:1800200422" style={link}>1800 200 422</a> (Mon–Fri 8am–8pm, Sat 10am–2pm)
          <br />
          Centrelink Older Australians line — <a href="tel:132300" style={link}>132 300</a>
          <br />
          WA Seniors Card Centre — <a href="tel:1800671233" style={link}>1800 671 233</a>
          <br />
          Older Persons Advocacy Network (OPAN) — <a href="tel:1800700600" style={link}>1800 700 600</a>
          <br />
          National Elder Abuse hotline — <a href="tel:1800353374" style={link}>1800 353 374</a>
        </p>
      </div>

      <h2 style={h2}>Start with My Aged Care</h2>
      <p style={p}>
        <strong>My Aged Care</strong> is the single front door for all
        Australian Government–funded aged care services — in-home help,
        residential care, respite, transport, and home modifications. You can't
        access subsidised aged care without going through My Aged Care first.
      </p>
      <ol style={{ ...p, paddingLeft: '1.4rem' }}>
        <li style={{ marginBottom: '0.5rem' }}>
          Phone <a href="tel:1800200422" style={link}>1800 200 422</a> or
          register at{' '}
          <a href="https://www.myagedcare.gov.au/" style={link}>
            myagedcare.gov.au
          </a>
          . An assessor (RAS for entry-level, ACAT for more complex needs)
          arranges a free in-home or phone assessment.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          You receive an outcome letter listing what you're approved for —
          Commonwealth Home Support, Support at Home, residential care, or
          respite.
        </li>
        <li style={{ marginBottom: '0.5rem' }}>
          You choose providers and start services. There can be long waits
          between approval and assignment — apply <em>before</em> you
          need full support, not after.
        </li>
      </ol>

      <h2 style={h2}>Commonwealth Home Support Programme (CHSP)</h2>
      <p style={p}>
        CHSP is the entry-level program — small amounts of help (a few hours of
        cleaning, meals, transport to medical appointments, garden
        maintenance, social groups) that let people stay in their own home
        longer. Most providers in Warren-Blackwood deliver CHSP locally
        through Silver Chain, the local shire, or community resource centres.
      </p>

      <h2 style={h2}>Support at Home</h2>
      <p style={p}>
        Support at Home started on 1 November 2025 and replaced Home Care
        Packages. The Commonwealth Home Support Programme (CHSP) will move
        into Support at Home no earlier than 1 July 2027. Contact My Aged
        Care to be assessed.
      </p>
      <p style={p}>
        <a href="https://www.health.gov.au/our-work/support-at-home" style={link}>
          Support at Home — health.gov.au →
        </a>
      </p>


      <h2 style={h2}>Residential aged care in Warren-Blackwood</h2>
      <p style={p}>
        Each shire's dashboard has the local list of residential facilities,
        in-home services, and community seniors programs — keep one click
        away.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem', listStyle: 'none' }}>
        {SHIRE_QUICK.map((s) => (
          <li key={s.id} style={{ marginBottom: '0.4rem' }}>
            <Link
              to="/shire/$name"
              params={{ name: s.id }}
              hash={`seniors-${s.id}`}
              style={link}
            >
              Shire of {s.name} — seniors services →
            </Link>
          </li>
        ))}
      </ul>
      <p style={p}>
        For a national search of every residential facility, including current
        room availability and quality ratings, use the{' '}
        <a href="https://www.myagedcare.gov.au/find-a-provider" style={link}>
          My Aged Care provider finder →
        </a>
      </p>

      <h2 style={h2}>Centrelink — Age Pension &amp; concessions</h2>
      <p style={p}>
        The Age Pension is administered by Services Australia (Centrelink). You
        can apply up to 13 weeks before reaching pension age. The pension is
        income- and assets-tested.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          Centrelink Older Australians line — <a href="tel:132300" style={link}>132 300</a>
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Apply online via{' '}
          <a href="https://www.servicesaustralia.gov.au/age-pension" style={link}>
            servicesaustralia.gov.au/age-pension
          </a>
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          If you're not yet of pension age but can't work due to disability,
          ask about JobSeeker (over 55) or the Disability Support Pension.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          The <strong>Commonwealth Seniors Health Card</strong> is for
          self-funded retirees who don't qualify for the Age Pension — it gives
          access to cheaper PBS medicines and bulk-billing incentives.
        </li>
      </ul>

      <h2 style={h2}>WA Seniors Card</h2>
      <p style={p}>
        The <strong>WA Seniors Card</strong> is the state government's discount
        and concessions card for residents aged 60+ working less than 25 hours
        a week. It's separate from the federal Pensioner Concession Card.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          Free public transport during off-peak hours, and reduced fares
          otherwise.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Cost of Living Rebate paid annually.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Concessions on water, motor vehicle licensing, drivers licence and a
          range of state government fees.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          Discounts at participating businesses — the directory is updated
          regularly on the Seniors Card website.
        </li>
      </ul>
      <p style={p}>
        Apply free — phone <a href="tel:1800671233" style={link}>1800 671 233</a>{' '}
        or visit{' '}
        <a href="https://www.seniorscard.wa.gov.au/" style={link}>
          seniorscard.wa.gov.au →
        </a>
      </p>

      <h2 style={h2}>Advocacy and elder abuse support</h2>
      <p style={p}>
        Older people in the south-west sometimes face pressure from family or
        carers around money, housing, or aged care decisions. These services
        are free and confidential.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Older Persons Advocacy Network (OPAN)</strong> —{' '}
          <a href="tel:1800700600" style={link}>1800 700 600</a> — advocacy on
          aged care services, in WA delivered by Advocare.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>National Elder Abuse hotline</strong> —{' '}
          <a href="tel:1800353374" style={link}>1800 353 374</a>.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Advocare WA</strong> —{' '}
          <a href="https://www.advocare.org.au/" style={link}>advocare.org.au</a> — WA's
          independent advocacy service for older people and aged care advocacy.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>COTA WA</strong> —{' '}
          <a href="https://www.cotawa.org.au/" style={link}>cotawa.org.au</a> — peak
          representative body for older Western Australians.
        </li>
      </ul>

      <h2 style={h2}>When the MP's office can help</h2>
      <p style={p}>
        Aged care is largely a Commonwealth responsibility, but the state and
        federal systems intersect constantly. Bevan advocates on aged care
        issues at state level — including the points where WA Health,
        Department of Communities and Commonwealth providers all meet.
      </p>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Hospital-to-aged-care transitions</strong> — where an older
          person is medically ready to leave hospital but has nowhere to go and
          no funded supports yet in place.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Support at Home wait times</strong> — escalating cases
          where someone has been approved for months and not yet had support
          assigned.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Local provider thin markets</strong> — where no provider in
          our region offers the services you're approved for.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Social housing for seniors</strong> — Department of
          Communities applications, transfers and modifications.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>Aged care quality concerns</strong> — alongside the official
          complaints channel (Aged Care Quality &amp; Safety Commission on{' '}
          <a href="tel:1800951822" style={link}>1800 951 822</a>).
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <strong>State-funded concessions and rebates</strong> — Seniors
          Card, energy assistance, water and rates concessions.
        </li>
      </ul>
      <p style={p}>
        To raise an issue, see the{' '}
        <Link to="/contact" style={link}>contact page</Link> for office hours
        and the best way to reach the team.
      </p>

      <h2 style={h2}>Trusted sources</h2>
      <ul style={{ ...p, paddingLeft: '1.2rem' }}>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.myagedcare.gov.au/" style={link}>myagedcare.gov.au</a> — the official aged care portal.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.servicesaustralia.gov.au/older-australians" style={link}>servicesaustralia.gov.au/older-australians</a> — Centrelink for seniors.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.seniorscard.wa.gov.au/" style={link}>seniorscard.wa.gov.au</a> — WA Seniors Card.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://www.agedcarequality.gov.au/" style={link}>agedcarequality.gov.au</a> — make a complaint about a provider.
        </li>
        <li style={{ marginBottom: '0.4rem' }}>
          <a href="https://opan.org.au/" style={link}>opan.org.au</a> — Older Persons Advocacy Network.
        </li>
      </ul>

      <p
        style={{
          marginTop: 'var(--sp-lg)',
          fontSize: '0.78rem',
          color: 'var(--ink-light)',
          borderTop: '1px solid var(--rule)',
          paddingTop: 'var(--sp-sm)',
          lineHeight: 1.5,
        }}
      >
        This page is a community summary of publicly-available information from
        My Aged Care, Services Australia and listed providers. Programs change
        — confirm details on the official sites above before acting. Spot
        something out of date?{' '}
        <a
          href="mailto:bevan.eatts@mp.wa.gov.au?subject=Warren-Blackwood%20guide%20%E2%80%94%20seniors%20page%20update"
          style={{ color: 'var(--green-mid)', fontWeight: 500 }}
        >
          Email the office
        </a>
        . Last reviewed: June 2026.
      </p>
    </main>
  );
}
